import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Paper,
  Modal,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import { useNavigate, useLocation, useParams } from "react-router-dom";

const EditPeriod = () => {
  const [formValues, setFormValues] = useState({
    id: "",
    startTime: "", // 24-hour: "13:00"
    endTime: "",
    periodSequence: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [canEdit, setCanEdit] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const { encodedFormId } = useParams();

  // Parse "07:00AM" or "01:00PM" → "07:00" or "13:00"
  const parse12HourTo24 = (time12) => {
    if (!time12) return "";
    const match = time12.match(/^(\d{2}):(\d{2})(AM|PM)$/i);
    if (!match) return time12.slice(0, 5);
    let [_, hours, minutes, period] = match;
    hours = parseInt(hours, 10);
    if (period.toUpperCase() === "PM" && hours !== 12) hours += 12;
    if (period.toUpperCase() === "AM" && hours === 12) hours = 0;
    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  };

  // Convert "13:00" → "01:00PM"
  const to12HourFormat = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;
    return `${hours12.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}${period}`;
  };

  useEffect(() => {
    const fetchPeriod = async () => {
      const periodId = location.state?.id;
      if (!periodId) {
        setError("No period selected");
        return;
      }

      try {
        const res = await fetch(
          "https://wsmsv1.aidigitalsec.com/api/TimeTable/GetPeriod",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: sessionStorage.getItem("token"),
            },
          }
        );
        const data = await res.json();
        const period = data.find((p) => p.periodId === periodId);

        if (period) {
          setFormValues({
            id: period.periodId,
            startTime: parse12HourTo24(period.startTime), // "01:00PM" → "13:00"
            endTime: parse12HourTo24(period.endTime),
            periodSequence: period.sequenceNo,
          });
        }
      } catch (err) {
        setError("Failed to load period");
      }
    };

    fetchPeriod();
  }, [location.state]);

  const handleUpdate = async () => {
    setLoading(true);
    setError("");

    const payload = {
      startTime: to12HourFormat(formValues.startTime),
      endTime: to12HourFormat(formValues.endTime),
      sequenceNo: parseInt(formValues.periodSequence),
    };

    try {
      const res = await fetch(
        `https://wsmsv1.aidigitalsec.com/api/TimeTable/UpdatePeriod/${formValues.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error("Update failed");
      setShowSuccessModal(true);
    } catch (err) {
      setError("Failed to update period");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Box
        sx={{
          backgroundColor: "#0B1F40",
          padding: "12px 24px",
          borderRadius: "4px 4px 0 0",
          mb: 1,
        }}
      >
        <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>
          Edit Period
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 4, pt: 6 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <TextField
            label="Start Time"
            type="time"
            value={formValues.startTime}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, startTime: e.target.value }))
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={!canEdit}
          />
          <TextField
            label="End Time"
            type="time"
            value={formValues.endTime}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, endTime: e.target.value }))
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={!canEdit}
          />
        </Box>

        <TextField
          label="Period Sequence"
          value={formValues.periodSequence}
          onChange={(e) =>
            setFormValues((prev) => ({
              ...prev,
              periodSequence: e.target.value,
            }))
          }
          fullWidth
          sx={{ mb: 3 }}
          disabled={!canEdit}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Tooltip title={!canEdit ? "Not authorized to edit" : ""}>
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdate}
                disabled={loading || !canEdit}
                sx={{ width: 110, height: 50, fontWeight: "bold" }}
              >
                {loading ? "UPDATING..." : "UPDATE"}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            color="error"
            onClick={() => navigate(-1)}
            sx={{ width: 110, height: 50, fontWeight: "bold" }}
          >
            CANCEL
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError("")}
      >
        <Alert severity="error">{error}</Alert>
      </Snackbar>

      <Modal open={showSuccessModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Box sx={{ bgcolor: "#0B1F40", p: 2 }}>
            <Typography variant="h6" color="white">
              Period Updated
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Typography>Period updated successfully.</Typography>
            <Box sx={{ mt: 3, textAlign: "right" }}>
              <Button
                variant="contained"
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default EditPeriod;
